import { motion } from 'framer-motion';
import { useCallback, useRef, useMemo } from 'react';

import { clsm } from '../../utils';
import {
  Provider as NotificationProvider
  // useNotif
} from '../../contexts/Notification';
import { Provider as ChatProvider } from '../../contexts/Chat';
import { Provider as PlayerProvider } from './contexts/Player';
import { BREAKPOINTS } from '../../constants';
import { useChannel } from '../../contexts/Channel';
import { useChannelView } from './contexts/ChannelView';
import { useLayoutEffect } from 'react';
import { useProfileViewAnimation } from './contexts/ProfileViewAnimation';
import { useResponsiveDevice } from '../../contexts/ResponsiveDevice';
import Chat from './Chat';
import PageUnavailable from '../../components/PageUnavailable';
import Player from './Player';
import Tabs from '../../components/Tabs/Tabs';
import useMount from '../../hooks/useMount';
import useResize from '../../hooks/useResize';
import Notification from '../../components/Notification/Notification';
// import { useUser } from '../../contexts/User';

const CHAT_PANEL_TAB_INDEX = 0;

const Channel = () => {
  const { channelError } = useChannel();
  const { isLandscape, isMobileView, currentBreakpoint } =
    useResponsiveDevice();
  // const { userData } = useUser();

  // Refs
  const channelRef = useRef();
  const chatSectionRef = useRef();

  // Real-time stage
  const shouldDisplayStagePlayer = false;

  const isChannelPageStackedView = currentBreakpoint < BREAKPOINTS.lg;

  // Channel UI
  const isMounted = useMount();
  // const { notifyError, notifySuccess, notifyNeutral } = useNotif();
  const { isStackedView, isSplitView } = useChannelView();
  const { getProfileViewAnimationProps, chatAnimationControls } =
    useProfileViewAnimation();
  const isTabView = isChannelPageStackedView;

  const visibleChatWidth = useMemo(() => {
    let width = 400;
    if (isSplitView) width = 308;
    else if (isStackedView) width = '100%';

    return width;
  }, [isSplitView, isStackedView]);

  /**
   * Chat section height
   */
  const updateChatSectionHeight = useCallback(() => {
    let chatSectionHeight = 200;

    if (isStackedView) {
      /**
       * When switching between mobile landscape and portrait modes, channelWidth may not be accurate.
       * Therefore, we use the window.innerHeight instead; otherwise, we use the channel width.
       */
      const { innerWidth, innerHeight } = window;
      const { clientWidth: channelWidth = 0 } = channelRef?.current || {};
      const width = isMobileView ? innerWidth : channelWidth;

      chatSectionHeight = Math.max(innerHeight - (width * 9) / 16, 200); // chat section should be no less than 200px in height
    }

    if (chatSectionRef.current) {
      chatSectionRef.current.style.minHeight = `${chatSectionHeight}px`;
    }
  }, [isMobileView, isStackedView]);

  // changes

  useResize(updateChatSectionHeight, { shouldCallOnMount: true });
  // Ensures we have computed and set the chat section min-height before the first render
  useLayoutEffect(() => {
    if (!isMounted()) updateChatSectionHeight();
  }, [isMounted, updateChatSectionHeight]);

  if (channelError) return <PageUnavailable />;

  return (
    <PlayerProvider>
      <Notification />
      <div
        className={clsm([
          'flex',
          'items-center',
          'justify-center',
          /* Default View */
          'w-full',
          'h-screen',
          'flex-row',
          /* Stacked View */
          'lg:flex-col',
          'lg:h-full',
          'lg:min-h-screen',
          'overflow-hidden',
          /* Split View */
          isLandscape && [
            'md:flex-row',
            'md:h-screen',
            'touch-screen-device:lg:flex-row',
            'touch-screen-device:lg:h-screen'
          ]
        ])}
        ref={channelRef}
      >
        <Player
          chatSectionRef={chatSectionRef}
          stagePlayerVisible={shouldDisplayStagePlayer}
        />
        <motion.section
          {...getProfileViewAnimationProps(
            chatAnimationControls,
            {
              desktop: {
                collapsed: { width: 400 },
                expanded: { width: 'auto' }
              },
              stacked: { width: 'auto' },
              split: { collapsed: { width: 308 }, expanded: { width: 0 } }
            },
            {
              visible: { width: visibleChatWidth },
              hidden: { width: 0 }
            }
          )}
          className={clsm([
            'relative',
            'flex',
            'shrink-0',
            'overflow-hidden',
            /* Default View */
            'h-screen',
            /* Stacked View */
            'lg:h-full',
            /* Split View */
            isLandscape && [
              'md:h-screen',
              'md:min-h-[auto]',
              'touch-screen-device:lg:w-[308px]',
              'touch-screen-device:lg:h-screen',
              'touch-screen-device:lg:min-h-[auto]'
            ],
            isStackedView && '!w-full'
          ])}
        >
          <div
            ref={chatSectionRef}
            className={clsm([
              'relative',
              'flex',
              'bg-white',
              'dark:bg-darkMode-gray-dark',
              /* Default View */
              'min-w-[400px]',
              /* Stacked View */
              'lg:min-w-full',
              /* Split View */
              isLandscape && [
                'md:min-w-[308px]',
                'touch-screen-device:lg:min-w-[308px]'
              ]
            ])}
          >
            <Tabs>
              <Tabs.Panel
                index={0}
                selectedIndex={isTabView ? CHAT_PANEL_TAB_INDEX : 0}
              >
                <NotificationProvider>
                  <ChatProvider>
                    <Chat shouldRunCelebration={false} />
                  </ChatProvider>
                </NotificationProvider>
              </Tabs.Panel>
            </Tabs>
          </div>
        </motion.section>
      </div>
    </PlayerProvider>
  );
};

export default Channel;
